import { RangeEntry, RangeEntryWrapper } from '../../Types'
import { StringUtils } from '../../../../../../../NonReact/utils/StringUtils'
import { dedupeAndCleanRangeEntryWrappers } from '../DepdupeAndCleanRangeEntryWapper/DedupeAndCleanRangeEntryWrappers'
import { combineSequentialNumbersIntoRanges } from '../CombineSequentialNumbersIntoRanges/CombineSequentialNumbersIntoRanges'
import { combineOverlappingOrSequentialRanges } from '../CombineOverlappingOrSequentialRanges/CombineOverlappingOrSequentialRanges'
import { IsVarEmpty } from '../../../../../../../NonReact/utils/GlobalFunctions'
import { DASH, EMPTY_STRING } from '../../../../../../../NonReact/constants/Common'
import { processDisplayValue } from '../Utils/Utilities'

const collapseRangeIfPossible = ({
    included,
    isRange,
    value: { minDisplayValue, min, maxDisplayValue, max }
}: RangeEntry): RangeEntryWrapper => {
    if (!isRange || min !== max) {
        return {
            included,
            isRange,
            value: { minDisplayValue, min, maxDisplayValue, max }
        }
    }

    return { included, isRange: false, displayValue: minDisplayValue, value: min }
}

/**
 * Remove Range Entry Wrapper from given string value.
 * This will remove the value or split ranges into two if the value is a range.
 *
 * @param value - The value to remove.
 * @param ranges - The ranges to remove the value from.
 *
 * @returns - The new ranges with the value removed.
 */
export const removeRangeEntryWrapper = (
    value: string,
    ranges: Nullable<RangeEntryWrapper[]>
): Nullable<RangeEntryWrapper[]> => {
    // Remove single entry from ranges.
    const newRanges: Nullable<RangeEntryWrapper[]> = ranges?.filter(
        (entry: RangeEntryWrapper): boolean => entry.isRange || (!entry.isRange && value !== entry.displayValue)
    )

    // If the single entry was removed, return the new ranges.
    if (IsVarEmpty(ranges) || newRanges?.length === 0 || ranges?.length !== newRanges?.length) {
        return newRanges
    }

    // If the entry is contained within a range, split the range into two.
    const valueAsNumber: Nullable<number | string> = StringUtils.convertToNumberIfPossible(value)

    if (IsVarEmpty(valueAsNumber) || typeof valueAsNumber === 'string') {
        return newRanges
    }

    const splitRanges: RangeEntryWrapper[] = ranges.reduce(
        (accum: RangeEntryWrapper[], next: RangeEntryWrapper): RangeEntryWrapper[] => {
            if (next.isRange) {
                if (next.value.minDisplayValue === value) {
                    const newMin: number = valueAsNumber + 1

                    accum.push(
                        collapseRangeIfPossible({
                            included: next.included,
                            isRange: true,
                            value: {
                                minDisplayValue: processDisplayValue(next.value.minDisplayValue, newMin),
                                min: newMin,
                                maxDisplayValue: next.value.maxDisplayValue,
                                max: next.value.max
                            }
                        })
                    )
                } else if (next.value.maxDisplayValue === value) {
                    const newMax: number = valueAsNumber - 1

                    accum.push(
                        collapseRangeIfPossible({
                            included: next.included,
                            isRange: true,
                            value: {
                                minDisplayValue: next.value.minDisplayValue,
                                min: next.value.min,
                                maxDisplayValue: processDisplayValue(next.value.maxDisplayValue, newMax),
                                max: newMax
                            }
                        })
                    )
                } else if (
                    next.value.min < valueAsNumber &&
                    next.value.max > valueAsNumber &&
                    processDisplayValue(next.value.minDisplayValue, valueAsNumber) === value
                ) {
                    accum.push(
                        collapseRangeIfPossible({
                            included: next.included,
                            isRange: true,
                            value: {
                                minDisplayValue: next.value.minDisplayValue,
                                min: next.value.min,
                                maxDisplayValue: processDisplayValue(next.value.maxDisplayValue, valueAsNumber - 1),
                                max: valueAsNumber - 1
                            }
                        })
                    )
                    accum.push(
                        collapseRangeIfPossible({
                            included: next.included,
                            isRange: true,
                            value: {
                                minDisplayValue: processDisplayValue(next.value.minDisplayValue, valueAsNumber + 1),
                                min: valueAsNumber + 1,
                                maxDisplayValue: next.value.maxDisplayValue,
                                max: next.value.max
                            }
                        })
                    )
                } else {
                    accum.push(next)
                }
            } else {
                accum.push(next)
            }

            return accum
        },
        []
    )

    const dedupedRanges: RangeEntryWrapper[] = dedupeAndCleanRangeEntryWrappers(splitRanges)
    const combinedSequentialRanges: RangeEntryWrapper[] = combineSequentialNumbersIntoRanges(dedupedRanges)
    const combinedRanges: RangeEntryWrapper[] = combineOverlappingOrSequentialRanges(combinedSequentialRanges)

    return combinedRanges
}

/**
 * Add Range Entry Wrapper from new string value.
 *
 * @param value - The value to add.
 * @param isIncluded - Whether the value is included or excluded.
 * @param ranges - The ranges to add the value to.
 *
 * @returns - The new ranges with the value added.
 */
export const addRangeEntryWrapper = (
    value: string,
    isIncluded: boolean,
    ranges: Nullable<RangeEntryWrapper[]>
): RangeEntryWrapper[] => {
    const splitEntry: string[] = value.split(DASH)
    const isRange: boolean =
        value.includes(DASH) &&
        splitEntry.length === 2 &&
        !isNaN(Number(splitEntry[0])) &&
        !isNaN(Number(splitEntry[1]))
    const valueAsNumberIfPoss: Nullable<string | number> = StringUtils.convertToNumberIfPossible(value)
    const newRanges: Nullable<RangeEntryWrapper>[] = [
        ...(ranges ?? []),
        ...(isRange
            ? [
                  {
                      included: isIncluded,
                      isRange: true,
                      value: {
                          minDisplayValue: splitEntry[0],
                          min: Number(splitEntry[0]),
                          maxDisplayValue: splitEntry[1],
                          max: Number(splitEntry[1])
                      }
                  }
              ]
            : [
                  {
                      included: isIncluded,
                      isRange: false,
                      displayValue: value,
                      value: valueAsNumberIfPoss ?? EMPTY_STRING
                  }
              ])
    ] as RangeEntryWrapper[]
    const dedupedRanges: RangeEntryWrapper[] = dedupeAndCleanRangeEntryWrappers(newRanges)
    const combinedSequentialRanges: RangeEntryWrapper[] = combineSequentialNumbersIntoRanges(dedupedRanges)
    const combinedRanges: RangeEntryWrapper[] = combineOverlappingOrSequentialRanges(combinedSequentialRanges)

    return combinedRanges
}
